import { useDispatch, useSelector } from "react-redux";
import { useRef, useState, useEffect, useCallback } from "react";
import { Scene } from "@babylonjs/core/scene";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { useScale } from "common/utils";
import { useCanvas } from "features/courses/hook";
import { BuilderSceneManager } from "features/builder/engine";
import { actions, selectAllMapModel } from "features/builder/slice";
import { CAMERA_CONFIG, LIGHT_CONFIG } from "features/builder/constants";
import { useBuilderInput } from "features/builder/hooks/useBuilderInput";
import { useBuilderContext } from "features/builder/context/BuilderContext";
import { loadMesh } from "./loader";
import { createOuterNode } from "./util";
import { useDndScene } from "./useDndScene";

export const BuilderScene = ({
  scene,
  setScene,
}: {
  scene: Scene | null;
  setScene: (scene: Scene) => void;
}) => {
  // 新システム: useBuilderInput でポインターイベント処理
  useBuilderInput(scene);
  useDndScene(scene);
  const dispatch = useDispatch();
  const { scale, ratio } = useScale();
  const [reload, setReload] = useState(false);
  const [isFirst, setIsFirst] = useState(true);
  const elementRef = useRef<HTMLDivElement>(null);
  const { eventBus, setScene: setContextScene, setSceneManager, reloadKey } = useBuilderContext();
  const mapModels = useSelector(selectAllMapModel);

  useEffect(() => {
    if (isFirst) {
      setIsFirst(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger reload when reloadKey changes
  useEffect(() => {
    if (reloadKey > 0) {
      setReload((r) => !r);
    }
  }, [reloadKey]);

  const onSceneReady = useCallback(
    async (canvas: HTMLCanvasElement, engine: Engine, scene: Scene) => {
      setScene(scene);
      setContextScene(scene);

      // ライト設定
      const light = new HemisphericLight("light", new Vector3(0, 1, 0), scene);
      light.intensity = LIGHT_CONFIG.intensity;

      // カメラ設定
      const radius =
        ratio > 1
          ? scale > 0.7
            ? CAMERA_CONFIG.radius / scale
            : CAMERA_CONFIG.radius * 2 * scale
          : CAMERA_CONFIG.radius / scale;
      const camera = new ArcRotateCamera(
        "camera",
        CAMERA_CONFIG.alpha,
        CAMERA_CONFIG.beta,
        radius,
        new Vector3(CAMERA_CONFIG.target.x, CAMERA_CONFIG.target.y, CAMERA_CONFIG.target.z),
        scene,
      );
      camera.lowerBetaLimit = CAMERA_CONFIG.lowerBetaLimit;
      camera.upperBetaLimit = CAMERA_CONFIG.upperBetaLimit;
      camera.lowerRadiusLimit = radius - CAMERA_CONFIG.radiusOffset / scale;
      camera.upperRadiusLimit = radius + CAMERA_CONFIG.radiusOffset / scale;
      camera.attachControl(canvas, true);

      scene.clearColor = new Color4(0, 0, 0, 0);

      // BuilderSceneManager でグリッド初期化
      const sceneManager = new BuilderSceneManager(eventBus);
      sceneManager.initializeWithScene(scene);
      setSceneManager(sceneManager);

      // Load existing mapModels
      for (const model of mapModels) {
        try {
          const position = new Vector3(model.position.x, model.position.y, model.position.z);
          const mesh = await loadMesh(scene, model.filename, model.offset);
          const boundingInfo = mesh.getBoundingInfo();
          const bottomLayer = Math.round(model.position.y);

          // Set child mesh names and ids
          mesh.getChildMeshes().forEach((subMesh) => {
            if (subMesh.name.includes("Cube")) {
              subMesh.isVisible = false;
            }
            subMesh.id = model.id;
            subMesh.name = model.name;
          });

          mesh.id = model.id;
          mesh.name = model.name;
          mesh.metadata = {
            type: model.type,
            rotation: model.rotation,
            position: model.position,
            bottomLayer,
          };

          const outer = createOuterNode(scene, position);

          // Apply scaling
          if (model.scaling) {
            outer.scaling = new Vector3(model.scaling.x, model.scaling.y, model.scaling.z);
          }

          // Apply direction/rotation
          if (model.direction) {
            outer.rotation = new Vector3(
              0,
              Math.atan2(Number(model.direction.x), Number(model.direction.z)),
              0,
            );
          }

          mesh.parent = outer;

          // Create asset ground layer
          sceneManager.createAssetGround(
            boundingInfo.boundingBox,
            model.type,
            model.position,
            bottomLayer,
          );
        } catch (err) {
          console.error("Failed to load model:", model.filename, err);
        }
      }

      dispatch(actions.updateLoading(false));

      engine.runRenderLoop(() => scene.render());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reload, eventBus, setContextScene, setSceneManager, mapModels, dispatch],
  );

  useCanvas(
    elementRef,
    onSceneReady,
    (error) => {
      console.error(error);
    },
    reload,
  );

  return <div ref={elementRef} className="absolute w-full h-full pointer-events-none" />;
};
